// scheman/SchemaVoView.jsx
import React, { useEffect, useState } from 'react';
import { useIsAuthorized, useLoading } from '../contexts/GlobalStateContext';
import { fetchWithAuth } from '../services/api';
import VoStationsPanel from './VoStationsPanel';
import BattenburgLoader from '../common/BattenburgLoader';
import DraggableToolbar from '../common/DraggableToolbar';
import { getTodaysDate, getSchemaDates, getActiveUserNames } from '../services/UtilityServices';

const SchemaVoView = () => {
  const { isAuthorized } = useIsAuthorized();
  const { loading } = useLoading();

  const [availableVOs, setAvailableVOs] = useState([]);
  const [selectedVoId, setSelectedVoId] = useState('');
  const [date, setDate] = useState(getTodaysDate());
  const [activeUserNames, setActiveUserNames] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);

  const selectedVo = availableVOs.find(vo => String(vo.id) === String(selectedVoId)) || null;

  // Ladda verksamhetsområden
  useEffect(() => {
    if (!isAuthorized) return;
    fetchWithAuth('/api/verksamhetsomraden')
      .then(r => r.json())
      .then(setAvailableVOs)
      .catch(() => {});
  }, [isAuthorized]);

  // Online-användare — samma som statusraden i Scheman
  useEffect(() => {
    if (!isAuthorized) return;
    getActiveUserNames().then(setActiveUserNames);
    const interval = setInterval(() => {
      getActiveUserNames().then(setActiveUserNames);
    }, 60000);
    return () => clearInterval(interval);
  }, [isAuthorized]);

  // Datumlista för "Välj datum" — baserad på VO:ts första station
  useEffect(() => {
    const forstaStation = selectedVo?.stationer?.[0];
    if (!forstaStation) {
      setAvailableDates([]);
      return;
    }
    getSchemaDates(forstaStation)
      .then(setAvailableDates)
      .catch(() => setAvailableDates([]));
  }, [selectedVo]);

  const shiftDate = (deltaDays) => {
    const [y, m, d] = date.split('-').map(Number);
    const newDate = new Date(Date.UTC(y, m - 1, d + deltaDays));
    setDate(newDate.toISOString().split('T')[0]);
  };

  if (!isAuthorized) {
    return (
      <div className="tw-container tw-mx-auto">
        <div className="tw-p-1" />
        <div className="tw-mb-2 tw-w-full">
          <div className="tw-rounded-3xl tw-overflow-hidden tw-border tw-border-blue-500 tw-w-[1700px] tw-max-w-full">
            <div className="tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-text-white tw-px-4 tw-py-3">
              <h3 className="tw-m-0 tw-text-2xl tw-font-bold tw-text-center">Logga in för att se schemat</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Status-rad — samma layout som EditorWrapper i Scheman, med tillägg av VO-val */}
      <div className="tw-relative tw-flex tw-items-center tw-px-4 tw-py-1 tw-min-h-[36px]">
        {activeUserNames.length > 0 && (
          <span className="tw-text-sm tw-text-gray-700 tw-flex-shrink-0">
            👥 Online: {activeUserNames.join(', ')}
          </span>
        )}

        <div className="tw-flex tw-items-center tw-gap-2 tw-ml-4">
          <label htmlFor="vo-select" className="tw-text-sm tw-font-medium tw-text-gray-700">
            Välj VO:
          </label>
          <select
            id="vo-select"
            value={selectedVoId}
            onChange={(e) => setSelectedVoId(e.target.value)}
            className="tw-px-3 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
          >
            <option value="">-- Välj VO --</option>
            {availableVOs.map(vo => (
              <option key={vo.id} value={vo.id}>{vo.name}</option>
            ))}
          </select>
        </div>

        {selectedVo && availableDates.length > 0 && (
          <div className="tw-fixed tw-left-[17cm] tw-flex tw-items-center tw-gap-2">
            <label htmlFor="date-select" className="tw-text-sm tw-font-medium tw-text-gray-700">
              Välj datum:
            </label>
            <select
              id="date-select"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="tw-px-3 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
            >
              {availableDates.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        )}

        <div className="tw-fixed tw-left-[30cm] tw-min-h-[40px]">
          {loading && <BattenburgLoader width={160} left={'50%'} top={159} />}
        </div>
      </div>

      {!selectedVo && (
        <div className="schedule-editor-wrapper">
          <div className="tw-absolute tw-top-[5cm] tw-left-1/2 tw--translate-x-1/2 tw-flex tw-items-center tw-gap-4">
            <h1 className="tw-mt-4 tw-text-4xl tw-text-gray-600 tw-font-bold tw-whitespace-pre-line tw-text-center">
              Välj ett verksamhetsområde
            </h1>
          </div>
        </div>
      )}

      {selectedVo && <VoStationsPanel stations={selectedVo.stationer} date={date} />}

      {selectedVo && (
        <DraggableToolbar initialPosition={{ x: window.innerWidth / 2, y: 100 }}>
          <div className="tw-bg-transparent tw-flex tw-items-center tw-gap-4 tw-px-4 tw-py-2 tw-cursor-grab">
            <button
              onClick={() => shiftDate(-1)}
              className="tw-px-4 tw-py-2 tw-rounded-full tw-flex tw-items-center tw-gap-2 tw-font-medium tw-shadow-md tw-transition-colors tw-bg-gray-100 tw-text-gray-700 hover:tw-bg-gray-200"
            >
              <span className="tw-hidden sm:tw-inline">Föregående</span>
            </button>

            <button
              onClick={() => setDate(getTodaysDate())}
              className="tw-px-5 tw-py-2 tw-rounded-full tw-font-medium tw-shadow-md tw-transition-colors tw-bg-blue-500 tw-text-white hover:tw-bg-blue-600"
            >
              I dag
            </button>

            <button
              onClick={() => shiftDate(1)}
              className="tw-px-4 tw-py-2 tw-rounded-full tw-flex tw-items-center tw-gap-2 tw-font-medium tw-shadow-md tw-transition-colors tw-bg-gray-100 tw-text-gray-700 hover:tw-bg-gray-200"
            >
              <span className="tw-hidden sm:tw-inline">Nästa</span> →
            </button>
          </div>
        </DraggableToolbar>
      )}
    </>
  );
};

export default SchemaVoView;
